import { defineStore } from "pinia";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { scoreSheetFonts } from "@/config/fonts";

export const useScoresheet = defineStore("scoreSheetStore", {
    state: () => ({
        isGenerating: false,

        // ── School Info ──
        school: {
            arabicName:  "كلية ابن مسعود لتحفيظ القرآن والدراسات الإسلامية بباوتشي",
            englishName: "BN MAS'UD COLLEGE FOR TAHFIZ AND ISLAMIC STUDIES",
            address:     "Saleh Manga Primary School, Gwallaga Bauchi, Bauchi State",
            contact:     "bnmasudcollege@gmail.com | 08032981527 | 08036928527 | 08032607393",
        },

        // ── Logo ──
        logo: {
            size:            20,
            portraitMargin:  8,
            landscapeMargin: 12,
        },

        // ── Header Typography ──
        header: {
            arabicFontSize:  16,
            englishFontSize: 16,
            addressFontSize: 13,
            contactFontSize: 10,
            titleFontSize:   14,
            titleBoxWidth:   180,
            titleBoxHeight:  8,
            titleBoxRadius:  1.5,
            dividerStart:    80,
        },

        // ── Colors ──
        colors: {
            arabic:      [0,   0,   150],
            english:     [0,   100, 0  ],
            address:     [60,  60,  60 ],
            contact:     [80,  80,  80 ],
            title:       [0,   0,   0  ],
            titleBg:     [240, 248, 255],
            divider:     [200, 200, 200],
            tableText:   [0,   0,   0  ],
            tableLine:   [0,   0,   0  ],
            tableHeadBg: [255, 255, 255],
        },

        // ── Table Font Sizes — [simple, standard, compact, ultra] ──
        fontSize: {
            head: [10, 8,  5,  5 ],
            subj: [10, 9,  6,  5 ],
            assm: [10, 7,  5,  4 ],
            body: [10, 10, 7,  6 ],
        },

        // ── Column Widths — [simple, standard, compact, ultra] ──
        colWidth: {
            sn:    [12, 8,  6,  5 ],
            total: [16, 12, 8,  7 ],
            avg:   [16, 14, 12, 10],
            pos:   [16, 14, 12, 10],
        },

        // ── Name Column ──
        nameCol: {
            simple:   { min: 50, max: 80, factor: 2.5 },
            standard: { min: 40, max: 70, factor: 2.2 },
            compact:  { min: 30, max: 50, factor: 2.0 },
            ultra:    { min: 28, max: 45, factor: 1.8 },
        },

        // ── Subject Col Base Widths ──
        subjectCol: {
            standard: { min: 15, max: 30, base: 15, factor: 1.8 },
            compact:  { min: 6,  max: 15, base: 6,  factor: 1.2 },
            ultra:    { min: 5,  max: 10, base: 5,  factor: 0.8 },
        },

        // ── Simple Mode Min Assessment Col Width ──
        simpleAssessmentColMinW: 14,

        // ── Page Margins — [simple, standard, compact, ultra] ──
        pageMargin: [10, 5, 3, 3],

        // ── Cell Padding ──
        cellPadding: {
            simple:   2,
            standard: { top: 1.2, right: 0.8,  bottom: 1.2, left: 0.8  },
            compact:  { top: 0.6, right: 0.25, bottom: 0.6, left: 0.25 },
            ultra:    { top: 0.4, right: 0.15, bottom: 0.4, left: 0.15 },
        },

        lineWidth: 0.1,

        // ── Rotated header / row heights per tier ──
        rotatedHeaderH: { compact: 24, ultra: 28 },
        subHeaderH:     { compact: 5,  ultra: 5  },
        bodyRowH:       { compact: 6,  ultra: 5.5 },
    }),

    actions: {
        async generateScoreSheet({ classesData, assessments, termName, sessionYear, logo }) {
            this.isGenerating = true;

            try {
                const getOrientation = (cls) =>
                    (cls.selectedSubjects?.length ?? 0) <= 3 ? "portrait" : "landscape";

                const firstValid = classesData.find(c => (c.students?.length ?? 0) > 0);
                if (!firstValid) throw new Error("No classes with students found.");

                const doc = new jsPDF({ orientation: getOrientation(firstValid) });

                if (scoreSheetFonts?.length) {
                    await Promise.all(
                        scoreSheetFonts.map(async (f) => {
                            const res = await fetch(f.url);
                            const buf = await res.arrayBuffer();
                            let binary = "";
                            new Uint8Array(buf).forEach((b) => (binary += String.fromCharCode(b)));
                            doc.addFileToVFS(f.fileName, btoa(binary));
                            doc.addFont(f.fileName, f.fontName, f.weight);
                        })
                    );
                }

                const logoBase64   = await this.convertImgToBase64(logo);
                let firstPageDrawn = false;

                for (let c = 0; c < classesData.length; c++) {
                    const cls      = classesData[c];
                    const subjects = cls.selectedSubjects || [];
                    const students = (cls.students || []).slice().sort((a, b) =>
                        `${a.first_name} ${a.last_name}`.toLowerCase()
                            .localeCompare(`${b.first_name} ${b.last_name}`.toLowerCase())
                    );

                    if (!students.length) continue;

                    const orientation = getOrientation(cls);
                    const subjCount   = subjects.length;

                    // ── Tier — mirrors useResult logic exactly ──
                    const isSimple  = subjCount <= 3;
                    const isNormal  = subjCount >= 4 && subjCount <= 8;
                    const isCompact = subjCount >= 9 && subjCount <= 12;
                    const isUltra   = subjCount >= 13;

                    // modeIdx: 0=simple, 1=standard/normal, 2=compact, 3=ultra
                    const modeIdx = isSimple ? 0 : isNormal ? 1 : isCompact ? 2 : 3;

                    if (!firstPageDrawn) {
                        firstPageDrawn = true;
                    } else {
                        doc.addPage(undefined, orientation);
                    }

                    const pageWidth  = doc.internal.pageSize.getWidth();
                    const pageMargin = this.pageMargin[modeIdx];
                    const usableWidth = pageWidth - 2 * pageMargin;

                    // ── Assessment lookup ──
                    const getSubjectAssessments = (subjectId) =>
                        assessments.filter(a =>
                            a.subjects?.some(s =>
                                String(s.id) === String(subjectId) &&
                                String(s.pivot?.class_room_id) === String(cls.id)
                            )
                        );

                    // ── Render header ──
                    let y = await this._renderHeader(doc, cls, logoBase64, termName, sessionYear, orientation, pageWidth);

                    // ════════════════════════════════════════════════════════
                    // PATH A — ≤ 8 subjects: original autoTable, flat headers
                    // ════════════════════════════════════════════════════════
                    if (isSimple || isNormal) {
                        const fsHead = this.fontSize.head[modeIdx];
                        const fsSubj = this.fontSize.subj[modeIdx];
                        const fsAssm = this.fontSize.assm[modeIdx];
                        const fsBody = this.fontSize.body[modeIdx];

                        const SN_W    = this.colWidth.sn[modeIdx];
                        const TOTAL_W = this.colWidth.total[modeIdx];
                        const AVG_W   = this.colWidth.avg[modeIdx];
                        const POS_W   = this.colWidth.pos[modeIdx];

                        const maxNameLength = Math.max(
                            ...students.map(s => `${s.first_name} ${s.last_name} | ${s.id ?? "-"}`.length),
                            20
                        );
                        const nameCfg = isSimple ? this.nameCol.simple : this.nameCol.standard;
                        const NAME_W  = Math.max(nameCfg.min, Math.min(nameCfg.max, maxNameLength * nameCfg.factor));

                        const fixedWidth  = SN_W + NAME_W + TOTAL_W + AVG_W + POS_W;
                        const totalAssessmentCols = subjects.reduce((sum, s) =>
                            sum + (getSubjectAssessments(s.id).length || 1), 0
                        );

                        if (isSimple) {
                            // ── Simple (≤ 3): original simple path ──
                            const assessmentColW = Math.max(
                                this.simpleAssessmentColMinW,
                                (usableWidth - fixedWidth) / totalAssessmentCols
                            );
                            const columnStyles = {
                                0: { cellWidth: SN_W,   fontSize: fsHead, halign: "center" },
                                1: { cellWidth: NAME_W, fontSize: fsHead, halign: "left"   },
                            };
                            let col = 2;
                            subjects.forEach(s => {
                                const aCount = getSubjectAssessments(s.id).length || 1;
                                for (let i = 0; i < aCount; i++) columnStyles[col++] = { cellWidth: assessmentColW };
                            });
                            columnStyles[col++] = { cellWidth: TOTAL_W };
                            columnStyles[col++] = { cellWidth: AVG_W };
                            columnStyles[col]   = { cellWidth: POS_W };

                            const headRow1 = [
                                { content: "S/N",                 rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                                { content: "Name | Admission No", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                                ...subjects.map(s => ({
                                    content: s.arabic || s.name,
                                    colSpan: getSubjectAssessments(s.id).length || 1,
                                    styles:  { font: "Amiri-Bold", halign: "center", valign: "middle" },
                                })),
                                { content: "Total",    rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                                { content: "Average",  rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                                { content: "Position", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
                            ];
                            const headRow2 = subjects.flatMap(s => {
                                const sa = getSubjectAssessments(s.id);
                                return sa.length
                                    ? sa.map(a => ({ content: a.type?.name ?? a.name, styles: { halign: "center" } }))
                                    : [{ content: "Score", styles: { halign: "center" } }];
                            });
                            const bodyRows = students.map((stu, i) => [
                                i + 1,
                                `${stu.first_name} ${stu.last_name} | ${stu.id ?? "-"}`,
                                ...subjects.flatMap(s => Array(getSubjectAssessments(s.id).length || 1).fill("")),
                                "", "", "",
                            ]);

                            autoTable(doc, {
                                startY: y,
                                margin: { left: pageMargin, right: pageMargin },
                                head: [headRow1, headRow2],
                                body: bodyRows,
                                theme: "grid",
                                tableWidth: "auto",
                                styles: {
                                    font: "Lateef",
                                    fontSize: fsBody,
                                    cellPadding: this.cellPadding.simple,
                                    halign: "center",
                                    valign: "middle",
                                    lineColor: this.colors.tableLine,
                                    lineWidth: this.lineWidth,
                                    textColor: this.colors.tableText,
                                },
                                headStyles: {
                                    fillColor: this.colors.tableHeadBg,
                                    textColor: this.colors.tableText,
                                    font: "Lateef-Bold",
                                    fontStyle: "bold",
                                    lineColor: this.colors.tableLine,
                                    lineWidth: this.lineWidth,
                                },
                                columnStyles,
                                showHead: "firstPage",
                            });

                        } else {
                            // ── Normal (4–8): standard scaled path ──
                            const subjCfg = this.subjectCol.standard;
                            const subjectColWidths = subjects.map(s => {
                                const aCount     = getSubjectAssessments(s.id).length || 1;
                                const textLength = (s.arabic || s.name || "").trim().length;
                                const baseW      = Math.max(subjCfg.min, Math.min(subjCfg.max, subjCfg.base + textLength * subjCfg.factor));
                                return { aCount, baseW };
                            });
                            const totalBaseW  = subjectColWidths.reduce((sum, s) => sum + s.baseW, 0);
                            const scaleFactor = totalBaseW > (usableWidth - fixedWidth)
                                ? (usableWidth - fixedWidth) / totalBaseW
                                : 1;
                            const finalWidths = subjectColWidths.map(s => ({
                                aCount: s.aCount,
                                perCol: Math.max(10, (s.baseW * scaleFactor) / s.aCount),
                            }));

                            const columnStyles = {
                                0: { cellWidth: SN_W,   fontSize: fsHead, halign: "center" },
                                1: { cellWidth: NAME_W, fontSize: fsHead, halign: "left", overflow: "linebreak" },
                            };
                            let col = 2;
                            finalWidths.forEach(({ aCount, perCol }) => {
                                for (let i = 0; i < aCount; i++) columnStyles[col++] = { cellWidth: perCol };
                            });
                            columnStyles[col++] = { cellWidth: TOTAL_W };
                            columnStyles[col++] = { cellWidth: AVG_W };
                            columnStyles[col]   = { cellWidth: POS_W };

                            const tableWidth = fixedWidth + finalWidths.reduce((s, { aCount, perCol }) => s + aCount * perCol, 0);
                            const leftMargin = Math.max(pageMargin, (pageWidth - tableWidth) / 2);

                            const headRow1 = [
                                { content: "S/N",                 rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                                { content: "Name | Admission No", rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                                ...subjects.map(s => ({
                                    content: s.arabic || s.name,
                                    colSpan: getSubjectAssessments(s.id).length || 1,
                                    styles:  { font: "Amiri-Bold", fontSize: fsSubj, halign: "center", valign: "middle" },
                                })),
                                { content: "Total",    rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                                { content: "Average",  rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                                { content: "Position", rowSpan: 2, styles: { fontSize: fsHead, halign: "center", valign: "middle" } },
                            ];
                            const headRow2 = subjects.flatMap(s => {
                                const sa = getSubjectAssessments(s.id);
                                return sa.length
                                    ? sa.map(a => ({ content: a.type?.name ?? a.name, styles: { fontSize: fsAssm, halign: "center" } }))
                                    : [{ content: "Score", styles: { fontSize: fsAssm, halign: "center" } }];
                            });
                            const bodyRows = students.map((stu, i) => [
                                i + 1,
                                `${stu.first_name} ${stu.last_name} | ${stu.id ?? "-"}`,
                                ...subjects.flatMap(s => Array(getSubjectAssessments(s.id).length || 1).fill("")),
                                "", "", "",
                            ]);

                            autoTable(doc, {
                                startY: y,
                                margin: { left: leftMargin },
                                head: [headRow1, headRow2],
                                body: bodyRows,
                                theme: "grid",
                                styles: {
                                    font: "Amiri",
                                    fontSize: fsBody,
                                    cellPadding: this.cellPadding.standard,
                                    halign: "center",
                                    valign: "middle",
                                    lineColor: this.colors.tableLine,
                                    lineWidth: this.lineWidth,
                                    textColor: this.colors.tableText,
                                },
                                headStyles: {
                                    font: "Lateef-Bold",
                                    fillColor: this.colors.tableHeadBg,
                                    textColor: this.colors.tableText,
                                    lineColor: this.colors.tableLine,
                                    lineWidth: this.lineWidth,
                                },
                                columnStyles,
                                showHead: "firstPage",
                            });
                        }

                        continue; // next class
                    }

                    // ════════════════════════════════════════════════════════
                    // PATH B — 9-12 (compact) or 13+ (ultra): manual draw
                    //          rotated subject headers, blank body cells
                    // ════════════════════════════════════════════════════════

                    // Always separate col per assessment for all tiers (compact + ultra)
                    const mergeAssessments = false;

                    const fsHead = this.fontSize.head[modeIdx];
                    const fsBody = this.fontSize.body[modeIdx];

                    const SN_W    = this.colWidth.sn[modeIdx];
                    const TOTAL_W = this.colWidth.total[modeIdx];
                    const AVG_W   = this.colWidth.avg[modeIdx];
                    const POS_W   = this.colWidth.pos[modeIdx];

                    const maxNameLength = Math.max(
                        ...students.map(s => `${s.first_name} ${s.last_name} | ${s.id ?? "-"}`.length),
                        20
                    );
                    const nameCfg = isUltra ? this.nameCol.ultra : this.nameCol.compact;
                    const NAME_W  = Math.max(nameCfg.min, Math.min(nameCfg.max, maxNameLength * nameCfg.factor));

                    const fixedW = SN_W + NAME_W + TOTAL_W + AVG_W + POS_W;

                    const subjAssessments = subjects.map(s => getSubjectAssessments(s.id));

                    const totalScoreCols = subjects.reduce((sum, s, si) =>
                        sum + (subjAssessments[si].length || 1), 0
                    );

                    const subjCfg   = isUltra ? this.subjectCol.ultra : this.subjectCol.compact;
                    const scoreColW = Math.max(subjCfg.min, (usableWidth - fixedW) / totalScoreCols);

                    // ── Column metadata ──
                    const colMeta = [];
                    colMeta.push({ type: 'sn' });
                    colMeta.push({ type: 'name' });
                    subjects.forEach((subj, si) => {
                        const aList = subjAssessments[si];
                        if (!aList.length) {
                            // no assessments configured — single blank col
                            colMeta.push({ type: 'assessment', subjIdx: si, assmIdx: 0, assm: null });
                        } else {
                            aList.forEach((a, ai) => {
                                colMeta.push({ type: 'assessment', subjIdx: si, assmIdx: ai, assm: a });
                            });
                        }
                    });
                    colMeta.push({ type: 'total' });
                    colMeta.push({ type: 'avg' });
                    colMeta.push({ type: 'pos' });

                    const colWidths = colMeta.map(m => {
                        if (m.type === 'sn')    return SN_W;
                        if (m.type === 'name')  return NAME_W;
                        if (m.type === 'total') return TOTAL_W;
                        if (m.type === 'avg')   return AVG_W;
                        if (m.type === 'pos')   return POS_W;
                        return scoreColW;
                    });

                    const colX = [];
                    let cx = pageMargin;
                    colWidths.forEach(w => { colX.push(cx); cx += w; });
                    const tableWidth = cx - pageMargin;

                    const rotH   = isUltra ? this.rotatedHeaderH.ultra : this.rotatedHeaderH.compact;
                    const subH   = isUltra ? this.subHeaderH.ultra     : this.subHeaderH.compact;
                    const bodyRH = isUltra ? this.bodyRowH.ultra       : this.bodyRowH.compact;
                    const headerY = y;

                    // ── S/N, Name, Total, Avg, Pos — full-height span cells ──
                    doc.setDrawColor(0, 0, 0);
                    doc.setLineWidth(0.2);

                    const drawSpanCell = (xi, label) => {
                        const x = colX[xi], w = colWidths[xi];
                        doc.rect(x, headerY, w, rotH + subH, 'S');
                        doc.setFont("Lateef-Bold", "bold");
                        doc.setFontSize(fsHead);
                        doc.setTextColor(0, 0, 0);
                        const textY = headerY + (rotH + subH) / 2 + fsHead * 0.35 / 2;
                        doc.text(label, x + w / 2, textY, { align: 'center' });
                    };

                    drawSpanCell(0, 'S/N');
                    drawSpanCell(1, 'Name | Admission No');
                    const sumIdx = colMeta.reduce((acc, m, i) => {
                        if (['total', 'avg', 'pos'].includes(m.type)) acc[m.type] = i;
                        return acc;
                    }, {});
                    drawSpanCell(sumIdx.total, 'Total');
                    drawSpanCell(sumIdx.avg,   'Average');
                    drawSpanCell(sumIdx.pos,   'Position');

                    // ── Rotated subject labels ──
                    subjects.forEach((subj, si) => {
                        const firstColIdx = colMeta.findIndex(m =>
                            (m.type === 'merged' || m.type === 'assessment') && m.subjIdx === si
                        );
                        if (firstColIdx === -1) return;

                        const subjectCols = colMeta
                            .map((m, i) => ({ m, i }))
                            .filter(({ m }) => (m.type === 'merged' || m.type === 'assessment') && m.subjIdx === si);

                        const spanX  = colX[firstColIdx];
                        const spanW  = subjectCols.reduce((sum, { i }) => sum + colWidths[i], 0);
                        const cellCx = spanX + spanW / 2;
                        const cellCy = headerY + rotH / 2;

                        doc.setLineWidth(0.2);
                        doc.rect(spanX, headerY, spanW, rotH, 'S');

                        const k  = doc.internal.scaleFactor;
                        const pH = doc.internal.pageSize.getHeight();
                        doc.internal.write(
                            `q ` +
                            `1 0 0 1 ${(cellCx * k).toFixed(4)} ${((pH - cellCy) * k).toFixed(4)} cm ` +
                            `0 1 -1 0 0 0 cm ` +
                            `1 0 0 1 ${(-(cellCx * k)).toFixed(4)} ${(-((pH - cellCy) * k)).toFixed(4)} cm`
                        );
                        doc.setFont('Amiri-Bold', 'bold');
                        doc.setFontSize(fsHead);
                        doc.setTextColor(0, 0, 0);
                        doc.text(subj.arabic || subj.name || '', cellCx, cellCy, { align: 'center', baseline: 'middle' });
                        doc.internal.write('Q');
                    });

                    // Sub-header row: full assessment name (CA / Exam) per col
                    const subY = headerY + rotH;
                    doc.setLineWidth(0.1);
                    doc.rect(pageMargin, subY, tableWidth, subH, 'S');
                    colMeta.forEach((m, ci) => {
                        if (m.type !== 'assessment') return;
                        const label = m.assm?.type?.name ?? m.assm?.name ?? 'Score';
                        const x = colX[ci], w = colWidths[ci];
                        doc.rect(x, subY, w, subH, 'S');
                        doc.setFont("Lateef-Bold", "bold");
                        doc.setFontSize(fsHead - 1);
                        doc.setTextColor(0, 0, 0);
                        doc.text(label, x + w / 2, subY + subH / 2 + (fsHead - 1) * 0.35 / 2, { align: 'center' });
                    });

                    y = subY + subH;

                    // ── Body rows — all blank for teacher to fill ──
                    doc.setFont("Lateef", "normal");
                    doc.setFontSize(fsBody);
                    doc.setTextColor(0, 0, 0);

                    students.forEach((stu, rowIdx) => {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(0.1);
                        doc.rect(pageMargin, y, tableWidth, bodyRH, 'S');

                        colMeta.forEach((m, ci) => {
                            const x = colX[ci], w = colWidths[ci];
                            doc.rect(x, y, w, bodyRH, 'S');

                            // Only name and S/N get text — all score cols stay blank
                            if (m.type === 'sn') {
                                doc.setFont("Lateef", "normal");
                                doc.setFontSize(fsBody);
                                doc.text(String(rowIdx + 1), x + w / 2, y + bodyRH / 2 + fsBody * 0.35 / 2, { align: 'center' });
                            } else if (m.type === 'name') {
                                doc.setFontSize(fsBody - 0.5);
                                doc.setTextColor(0, 0, 0);
                                doc.text(
                                    `${stu.first_name} ${stu.last_name}`,
                                    x + 1, y + bodyRH * 0.38, { maxWidth: w - 2 }
                                );
                                doc.setFontSize(fsBody - 1.5);
                                doc.setTextColor(80, 80, 80);
                                doc.text(String(stu.id ?? ''), x + 1, y + bodyRH * 0.75);
                                doc.setTextColor(0, 0, 0);
                            }
                            // score / total / avg / pos — intentionally blank
                        });

                        y += bodyRH;

                        // Page break
                        const pageH = doc.internal.pageSize.getHeight();
                        if (y + bodyRH > pageH - 15) {
                            doc.addPage(undefined, orientation);
                            y = pageMargin;

                            const repH = subH + 2;
                            doc.setFillColor(230, 230, 230);
                            doc.rect(pageMargin, y, tableWidth, repH, 'F');
                            doc.setFont("Lateef-Bold", "bold");
                            doc.setFontSize(fsHead - 1);
                            doc.setTextColor(0, 0, 0);
                            colMeta.forEach((m, ci) => {
                                const x = colX[ci], w = colWidths[ci];
                                let lbl = '';
                                if (m.type === 'sn')              lbl = '#';
                                else if (m.type === 'name')       lbl = 'Name';
                                else if (m.type === 'assessment') lbl = m.assm?.type?.name ?? m.assm?.name ?? '';
                                else if (m.type === 'total')      lbl = 'Total';
                                else if (m.type === 'avg')        lbl = 'Avg';
                                else if (m.type === 'pos')        lbl = 'Pos';
                                doc.rect(x, y, w, repH, 'S');
                                doc.text(lbl, x + w / 2, y + repH / 2 + (fsHead - 1) * 0.35 / 2, { align: 'center' });
                            });
                            y += repH;
                            doc.setFont("Lateef", "normal");
                            doc.setFontSize(fsBody);
                        }
                    });

                } // end for each class

                const fileName = classesData.length === 1
                    ? `${classesData[0].name}_${termName}_${sessionYear}_Score_Sheet`.replace(/\s+/g, "_").toUpperCase()
                    : `${termName}_${sessionYear}_Score_Sheets`.replace(/\s+/g, "_").toUpperCase();

                doc.save(`${fileName}.pdf`);

            } finally {
                this.isGenerating = false;
            }
        },

        // ── Shared header renderer ──────────────────────────────────────────
        async _renderHeader(doc, cls, logoBase64, termName, sessionYear, orientation, pageWidth) {
            const margin = orientation === "portrait"
                ? this.logo.portraitMargin
                : this.logo.landscapeMargin;

            let y = 10;

            if (logoBase64) {
                doc.addImage(logoBase64, "JPEG", margin, y + 2, this.logo.size, this.logo.size);
                doc.addImage(logoBase64, "JPEG", pageWidth - margin - this.logo.size, y + 2, this.logo.size, this.logo.size);
            }

            y += orientation === "portrait" ? 6 : 4;

            doc.setFont("AeCortoba", "normal");
            doc.setFontSize(this.header.arabicFontSize);
            doc.setTextColor(...this.colors.arabic);
            doc.text(this.school.arabicName, pageWidth / 2, y + 5, { align: "center" });

            y += 10;
            doc.setFont("Lateef-Bold", "bold");
            doc.setFontSize(this.header.englishFontSize);
            doc.setTextColor(...this.colors.english);
            doc.text(this.school.englishName, pageWidth / 2, y, { align: "center" });

            y += 7;
            doc.setDrawColor(...this.colors.divider);
            doc.setLineWidth(0.3);
            doc.line(this.header.dividerStart, y, pageWidth - this.header.dividerStart, y);

            y += 6;
            doc.setFont("Lateef", "normal");
            doc.setFontSize(this.header.addressFontSize);
            doc.setTextColor(...this.colors.address);
            doc.text(this.school.address, pageWidth / 2, y, { align: "center" });

            y += 6;
            doc.setFontSize(this.header.contactFontSize);
            doc.setTextColor(...this.colors.contact);
            doc.text(this.school.contact, pageWidth / 2, y, { align: "center" });

            y += 8;
            doc.setFillColor(...this.colors.titleBg);
            doc.roundedRect(
                (pageWidth - this.header.titleBoxWidth) / 2,
                y - 5,
                this.header.titleBoxWidth,
                this.header.titleBoxHeight,
                this.header.titleBoxRadius,
                this.header.titleBoxRadius,
                "F"
            );
            doc.setFont("Lateef-Bold", "bold");
            doc.setFontSize(this.header.titleFontSize);
            doc.setTextColor(...this.colors.title);
            doc.text(
                `${cls.name} ${termName} ${sessionYear} SCORE SHEET`.toUpperCase(),
                pageWidth / 2, y, { align: "center" }
            );

            return y + 10;
        },

        async convertImgToBase64(img) {
            if (!img) return null;
            const res = await fetch(img);
            const buf = await res.arrayBuffer();
            let binary = "";
            new Uint8Array(buf).forEach((b) => (binary += String.fromCharCode(b)));
            return `data:image/jpeg;base64,${btoa(binary)}`;
        },
    },
});